<script lang="ts">
/** How a result value reads: a plain answer, an absent one, or one carrying
    risk. `warning` and `alert` are the two risk steps — views map their own
    severity onto these; the row never parses. */
export type GaiaResultTone = 'default' | 'muted' | 'warning' | 'alert';

/**
 * Whether the evaluation's action triggers did anything on this run: `fired`
 * marks a row whose actions ran, `idle` one that has actions configured that
 * this result did not fire. An evaluation with no actions at all passes neither
 * and gets no bolt.
 */
export type GaiaTriggerState = 'fired' | 'idle';
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaTooltip, { gaiaTooltipProps } from '../base/GaiaTooltip.vue';
import GaiaTriggerGlyph from './GaiaTriggerGlyph.vue';
import { gaiaNeutral, gaiaStatus } from '../themes/tokens';

const props = defineProps({
  /** The evaluation or extraction name. */
  label: {
    type: String,
    required: true,
  },
  value: {
    type: String,
    required: true,
  },
  tone: {
    type: String as PropType<GaiaResultTone>,
    default: 'default',
  },
  /** Flags the row's action triggers — see GaiaTriggerState. */
  trigger: {
    type: String as PropType<GaiaTriggerState>,
    default: undefined,
  },
});

const tones = {
  default: gaiaNeutral.text,
  muted: gaiaNeutral.textMuted,
  warning: gaiaStatus.warning,
  alert: gaiaStatus.alertText,
} as const;

const valueColor = computed(() => tones[props.tone]);

// Ink once actions have run, faint while they are only configured — the bolt is
// a flag either way, so the state is its colour rather than a second glyph.
const triggerColor = computed(() =>
  props.trigger === 'fired' ? gaiaNeutral.ink : gaiaNeutral.textFaint
);

const divider = gaiaNeutral.borderSoft;
</script>

<template>
  <!-- One result line in a run's detail (Playbooks mock): name left, answer
       right. Rows flow in a responsive multi-column grid in the view, so the
       row stays one line tall and an answer too long for its column clips.

       Fill `detail` (with GaiaRunResultDetail) and the whole row hovers to the
       grid view's bubble — outcome and reasoning — which is the answer in full
       as well, so the value's own reveal stands down. Rows with nothing more to
       say leave the slot empty and keep that reveal. -->
  <gaia-tooltip :disabled="!$slots.detail">
    <template #trigger>
      <n-flex
        align="baseline"
        :size="14"
        :wrap="false"
        :style="{ padding: '9px 0', borderBottom: `1px solid ${divider}` }"
      >
        <gaia-text size="dense" tone="tertiary" :style="{ flex: 1, minWidth: 0 }">
          {{ props.label }}
        </gaia-text>
        <gaia-text
          size="dense"
          weight="semibold"
          :style="{ maxWidth: '52%', textAlign: 'right', color: valueColor }"
        >
          <!-- The clipping is n-ellipsis's, not GaiaText's `truncate`, because it
               measures the answer and hands the clipped ones a hover tooltip with
               the full text — the same idiom the Grid view's cells use. The bubble
               takes GaiaTooltip's defaults; n-ellipsis wants tooltip props, not a
               component. Keep the slot plain text: it is cloned into the bubble, so
               an n-text there would carry this row's tone colour into it instead of
               the tooltip theme's own text colour. -->
          <n-ellipsis :tooltip="$slots.detail ? false : gaiaTooltipProps">
            {{ props.value }}
          </n-ellipsis>
        </gaia-text>
        <gaia-trigger-glyph
          v-if="props.trigger"
          :size="11"
          :color="triggerColor"
          :style="{ flex: 'none', alignSelf: 'center' }"
        />
      </n-flex>
    </template>
    <slot name="detail" />
  </gaia-tooltip>
</template>
